/* eslint-disable @typescript-eslint/no-unused-vars */
// ============================================================
// SIMADCA v3.0 – SensorMap.tsx FINAL (componentă unificată)
// Înlocuiește toate variantele de hartă din OverviewPanel etc.
// ============================================================

import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Tag } from 'antd';
import {
  EnvironmentOutlined, ReloadOutlined,
  FilterOutlined, ThunderboltOutlined, WifiOutlined,
} from '@ant-design/icons';

import { SensorData, SensorStatus } from '../types';
import { useSimulatorData } from '../context/SimulatorContext';
import { mockFarms } from '../data/mockData';

// Fix Leaflet default icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl:       require('leaflet/dist/images/marker-icon.png'),
  shadowUrl:     require('leaflet/dist/images/marker-shadow.png'),
});

// ─── Constante ────────────────────────────────────────────

const SC: Record<SensorStatus, string> = {
  online:      '#10b981',
  warning:     '#f59e0b',
  offline:     '#ef4444',
  calibrating: '#6366f1',
};
const SL: Record<SensorStatus, string> = {
  online:      'Online',
  warning:     'Avertisment',
  offline:     'Offline',
  calibrating: 'Calibrare',
};
const TC: Record<string, string> = {
  'FO-SPR':      '#6366f1',
  'SAW':         '#06b6d4',
  'Electrochimic': '#10b981',
};

// ─── Marker icon ──────────────────────────────────────────

const makeIcon = (color: string, pulse: boolean, size: number = 16) =>
  L.divIcon({
    className: '',
    html: `
      <div style="position:relative;width:${size + 4}px;height:${size + 4}px;">
        ${pulse ? `<span style="position:absolute;inset:0;border-radius:50%;background:${color};opacity:0.35;animation:ping 1.4s ease infinite;"></span>` : ''}
        <span style="position:absolute;top:2px;left:2px;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid #fff;box-shadow:0 2px 6px ${color}66;display:block;"></span>
      </div>
      <style>@keyframes ping{75%,100%{transform:scale(2.2);opacity:0}}</style>
    `,
    iconSize:    [size + 4, size + 4],
    iconAnchor:  [(size + 4) / 2, (size + 4) / 2],
    popupAnchor: [0, -size],
  });

// ─── Popup content ────────────────────────────────────────

const PopupContent: React.FC<{ sensor: SensorData }> = ({ sensor: s }) => {
  const overLod = s.value >= s.lod;
  const farm    = mockFarms.find(f => f.id === s.farmId);
  return (
    <div style={{ fontFamily: "'Inter', sans-serif", minWidth: 210, maxWidth: 240 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div style={{ width: 28, height: 28, borderRadius: 7, background: `${SC[s.status]}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: SC[s.status], flexShrink: 0 }}>
          <EnvironmentOutlined />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</div>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.type}</div>
        </div>
      </div>

      {/* Valoare */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', borderRadius: 8, background: overLod ? '#fff5f5' : '#f0fdf4', border: `1px solid ${overLod ? '#fecaca' : '#bbf7d0'}`, marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2 }}>{s.parameter}</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: overLod ? '#ef4444' : '#10b981', letterSpacing: '-0.3px' }}>
            {s.value.toFixed(4)} <span style={{ fontSize: 11, fontWeight: 500 }}>{s.unit}</span>
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#94a3b8' }}>LOD</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: '#f59e0b' }}>{s.lod} {s.unit}</div>
        </div>
      </div>

      {/* Status row */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
        <span style={{ width: 7, height: 7, borderRadius: '50%', background: SC[s.status], display: 'inline-block', flexShrink: 0 }} />
        <span style={{ fontSize: 11, color: '#64748b' }}>{SL[s.status]}</span>
        {s.batteryLevel !== undefined && (
          <span style={{ marginLeft: 'auto', fontSize: 10, color: s.batteryLevel < 20 ? '#ef4444' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 3 }}>
            <ThunderboltOutlined style={{ fontSize: 9 }} /> {s.batteryLevel}%
          </span>
        )}
        {s.signalStrength !== undefined && (
          <span style={{ fontSize: 10, color: s.signalStrength < 30 ? '#ef4444' : '#94a3b8', display: 'flex', alignItems: 'center', gap: 3 }}>
            <WifiOutlined style={{ fontSize: 9 }} /> {s.signalStrength}%
          </span>
        )}
      </div>

      {/* Locatie */}
      <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.4 }}>
        <div>{s.location.label}</div>
        {farm && <div style={{ color: '#6366f1', marginTop: 2 }}>{farm.name}</div>}
        <div style={{ marginTop: 2, color: '#cbd5e1' }}>
          {new Date(s.timestamp).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </div>
  );
};

// ─── Recenter control ─────────────────────────────────────

const RecenterControl: React.FC = () => {
  const map = useMap();
  return (
    <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000 }}>
      <button
        onClick={() => map.setView([45.9432, 24.9668], 6)}
        title="Recentrează harta"
        style={{ width: 30, height: 30, borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#64748b', boxShadow: '0 1px 4px rgba(0,0,0,0.12)' }}
      >
        <ReloadOutlined />
      </button>
    </div>
  );
};

// ─── Props ────────────────────────────────────────────────

interface SensorMapProps {
  height?:     number | string;
  showFilter?: boolean;
  showLegend?: boolean;
  filterFarmId?: string;
}

// ─── Component ────────────────────────────────────────────

const SensorMap: React.FC<SensorMapProps> = ({
  height     = 380,
  showFilter = true,
  showLegend = true,
  filterFarmId,
}) => {
  const { sensors } = useSimulatorData();
  const [activeStatuses, setActiveStatuses] = useState<Set<SensorStatus>>(
    new Set<SensorStatus>(['online', 'warning', 'offline', 'calibrating'])
  );
  const [activeType, setActiveType] = useState<string>('all');

  // Filtrare
  const displayed = sensors.filter(s => {
    const matchStatus = activeStatuses.has(s.status);
    const matchType   = activeType === 'all' || s.type === activeType;
    const matchFarm   = !filterFarmId || s.farmId === filterFarmId;
    return matchStatus && matchType && matchFarm;
  });

  const toggleStatus = (st: SensorStatus) => {
    setActiveStatuses(prev => {
      const next = new Set(prev);
      if (next.has(st)) { if (next.size > 1) next.delete(st); }
      else next.add(st);
      return next;
    });
  };

  const counts = (Object.keys(SC) as SensorStatus[]).map(k => ({
    key: k, label: SL[k], color: SC[k],
    count: sensors.filter(s => s.status === k && (!filterFarmId || s.farmId === filterFarmId)).length,
  }));

  // Centru harta — dacă e filtrat pe fermă, centrăm pe ea
  const farmCenter = filterFarmId
    ? mockFarms.find(f => f.id === filterFarmId)
    : null;
  const center: [number, number] = farmCenter
    ? [farmCenter.location.lat, farmCenter.location.lng]
    : [45.9432, 24.9668];
  const zoom = farmCenter ? 10 : 6;

  return (
    <div>
      {/* Filter bar */}
      {showFilter && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center',
          padding: '8px 12px', background: '#f8fafc',
          border: '1px solid #e2e8f0', borderRadius: showFilter ? '10px 10px 0 0' : 10,
          borderBottom: 'none',
        }}>
          <FilterOutlined style={{ fontSize: 12, color: '#94a3b8' }} />
          {/* Status filters */}
          {counts.map(c => (
            <button
              key={c.key}
              onClick={() => toggleStatus(c.key as SensorStatus)}
              style={{
                display: 'flex', alignItems: 'center', gap: 5,
                padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                border: `1.5px solid ${activeStatuses.has(c.key as SensorStatus) ? c.color : '#e2e8f0'}`,
                background: activeStatuses.has(c.key as SensorStatus) ? `${c.color}15` : '#fff',
                color: activeStatuses.has(c.key as SensorStatus) ? c.color : '#94a3b8',
                cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.15s',
              }}
            >
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.color, display: 'inline-block' }} />
              {c.label} ({c.count})
            </button>
          ))}

          {/* Tip senzor */}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            {['all', 'FO-SPR', 'SAW', 'Electrochimic'].map(t => (
              <button key={t} onClick={() => setActiveType(t)} style={{ padding: '3px 9px', borderRadius: 7, fontSize: 10, fontWeight: activeType === t ? 700 : 500, border: `1px solid ${activeType === t ? '#6366f1' : '#e2e8f0'}`, background: activeType === t ? '#eef2ff' : '#fff', color: activeType === t ? '#6366f1' : '#64748b', cursor: 'pointer', fontFamily: 'inherit' }}>
                {t === 'all' ? 'Toate' : t}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Harta */}
      <div style={{
        height, borderRadius: showFilter && showLegend ? '0' : showFilter ? '0 0 10px 10px' : showLegend ? '10px 10px 0 0' : '10px',
        overflow: 'hidden', border: '1px solid #e2e8f0',
        borderTop: showFilter ? 'none' : '1px solid #e2e8f0',
        borderBottom: showLegend ? 'none' : '1px solid #e2e8f0',
        position: 'relative',
      }}>
        <MapContainer
          center={center} zoom={zoom}
          scrollWheelZoom
          style={{ height: '100%', width: '100%' }}
          zoomControl
        >
          <TileLayer
            attribution='&copy; <a href="https://osm.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterControl />

          {displayed.map(sensor => {
            const pulse = sensor.status === 'warning' || (sensor.status === 'online' && sensor.value >= sensor.lod);
            return (
              <Marker
                key={sensor.id}
                position={[sensor.location.lat, sensor.location.lng]}
                icon={makeIcon(SC[sensor.status], pulse, 14)}
              >
                <Popup minWidth={220} maxWidth={250}>
                  <PopupContent sensor={sensor} />
                </Popup>
              </Marker>
            );
          })}
        </MapContainer>
      </div>

      {/* Legendă */}
      {showLegend && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center',
          padding: '8px 14px', background: '#f8fafc',
          border: '1px solid #e2e8f0', borderTop: '1px dashed #e2e8f0',
          borderRadius: '0 0 10px 10px',
        }}>
          <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Legendă:</span>
          {counts.map(c => (
            <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: c.color, display: 'inline-block', boxShadow: `0 0 0 3px ${c.color}20` }} />
              <span style={{ fontSize: 11, color: '#475569' }}>{c.label}</span>
              <span style={{ fontSize: 10, fontWeight: 700, padding: '0 5px', borderRadius: 99, background: `${c.color}12`, color: c.color }}>{c.count}</span>
            </div>
          ))}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: '#f59e0b', boxShadow: '0 0 0 4px rgba(245,158,11,0.2)' }} />
            <span style={{ fontSize: 10, color: '#94a3b8' }}>Pulsant = depășire activă</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensorMap;